import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { SuiClient } from '@mysten/sui/client';
import { Transaction } from '@mysten/sui/transactions';
import { toBase64 } from '@mysten/sui/utils';
import { getWallets, StandardConnectFeature, StandardDisconnectFeature, SuiReportTransactionEffectsFeature, SuiSignTransactionFeature } from '@mysten/wallet-standard';
import { ConnectModel } from '../models/connect.model';
import { ExecuteModel } from '../models/execute.model';
import { StorageAccountModel, StorageModel } from '../models/storage.model';
import { ChainHelper } from './chain.helper';
import { ToastHelper } from './toast.helper';
import { SettingHelper } from './setting.helper';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WalletHelper {

  /**
   * Chain helper
   */
  chain_helper = inject(ChainHelper);

  /**
   * Setting helper
   */
  setting_helper = inject(SettingHelper);

  /**
   * Toast helper
   */
  toast_helper = inject(ToastHelper);

  /**
   * Account coins balance
   */
  readonly coins_balance = signal(BigInt(0));

  /**
   * Account tokens balance
   */
  readonly tokens_balance = signal(BigInt(0));

  /**
   * Saved data store
   */
  readonly store: WritableSignal<StorageModel> = signal(new StorageModel(), { equal: () => false });

  /**
   * Active account
   */
  readonly active_account = computed(() => this.store().accounts.find(x => x.active));

  /**
   * Connected accounts
   */
  readonly connected_accounts = computed(() => this.store().accounts);

  /**
   * Saved storage
   */
  private storage = new StorageModel();

  /**
   * Read wallet details task
   */
  private read_details_task: any;

  /**
   * Start tasks
   */
  startTasks() {
    this.read_details_task = setInterval(() => { this.readBalances() }, 6000);
  }

  /**
   * Stop tasks
   */
  stopTasks() {
    if (this.read_details_task) {
      clearInterval(this.read_details_task);
    }
  }

  /**
   * Get list of wallets
   */
  listWallets() {
    let popular = ['Slush', 'Suiet', 'Phantom', 'Backpack'];
    let other: Array<string> = [];

    let installed = getWallets().get().filter(x => !popular.includes(x.name) && x.features['standard:connect'] && x.features['sui:signTransaction']);
    for (let i = 0; i < installed.length; i++) {
      other.push(installed[i].name);
    }

    return {
      popular: popular,
      other: other
    };
  }

  /**
   * Read storage
   */
  readStorage() {
    // Read wallet
    let wallet = localStorage.getItem('kyovy.wallet');
    this.storage.wallet = wallet;

    // Read accounts
    let accounts = localStorage.getItem('kyovy.accounts');
    if (accounts) {
      let results: Array<StorageAccountModel> = [];

      let values = [];
      try {
        let saved = JSON.parse(accounts);
        if (saved && Array.isArray(saved)) {
          values = saved;
        }
      } catch (error) { }

      for (let i = 0; i < values.length; i++) {
        let account = new StorageAccountModel();
        account.address = values[i].address;
        account.active = values[i].active;
        results.push(account);
      }

      this.storage.accounts = results;
    } else {
      this.storage.accounts = [];
    }

    this.store.set(this.storage);
  }

  /**
   * Change active account
   * 
   * @param address
   */
  setActive(address: string) {
    // Set active account
    let accounts = this.storage.accounts;
    for (let i = 0; i < accounts.length; i++) {
      accounts[i].active = false;
      if (accounts[i].address == address.toLowerCase()) {
        accounts[i].active = true;
      }
    }

    // Set default active account
    if (accounts.length > 0 && accounts.filter(a => a.active).length == 0) {
      accounts[0].active = true;
    }

    localStorage.setItem('kyovy.accounts', JSON.stringify(accounts));
    this.readStorage();
  }

  /**
   * Connect to wallet
   * 
   * @param id
   */
  async connect(id: string) {
    this.toast_helper.info('Connecting to ' + id);
    let response = await this.connectToWallet(id);
    if (response.success) {
      this.setWallet(id);
      this.setAccounts(response.accounts);
    } else {
      this.toast_helper.error(response.message);
    }
  }

  /**
   * Disconnect from wallet
   */
  async disconnect() {
    if (this.store().wallet) {
      let id = this.store()?.wallet;
      if (id) {
        await this.disconnectFromWallet(id);
      }
    }

    this.clearStorage();
  }

  /**
   * Execute transaction
   * 
   * @param sender
   * @param transaction
   */
  async execute(sender: string, transaction: Transaction): Promise<ExecuteModel> {
    let response = new ExecuteModel();

    if (this.store().wallet) {
      let id = this.store()?.wallet;
      if (id) {
        this.toast_helper.info('Preparing transaction');
        response = await this.executeTransaction(id, sender, transaction);
        if (response.success) {
          this.toast_helper.success('Transaction submitted successfully');
        } else {
          this.toast_helper.error(response.message);
        }
      }
    }

    return response;
  }

  /**
   * Clear storage
   */
  private clearStorage() {
    localStorage.clear();
    this.readStorage();
  }

  /**
   * Set currently selected wallet
   *
   * @param wallet
   */
  private setWallet(wallet: string) {
    localStorage.setItem('kyovy.wallet', wallet);
    this.readStorage();
  }

  /**
   * Set currently available accounts
   *
   * @param addresses
   */
  private setAccounts(addresses: Array<string>) {
    let accounts = [];

    // Populate account details
    for (let i = 0; i < addresses.length; i++) {
      let address = addresses[i].toLowerCase();

      let account = new StorageAccountModel();
      account.address = address;
      account.active = false;
      accounts.push(account);
    }

    // Set default active account
    if (accounts.length > 0 && accounts.filter(a => a.active).length == 0) {
      accounts[0].active = true;
    }

    localStorage.setItem('kyovy.accounts', JSON.stringify(accounts));
    this.readStorage();
  }

  /**
   * Read account balances
   */
  private async readBalances() {
    // Read account properties
    let address = this.active_account()?.address;
    if (address) {
      // Read coins balance
      let coins_balance = await this.chain_helper.readBalance(address, '0x2::sui::SUI');
      this.coins_balance.set(coins_balance);

      // Read tokens balance
      let tokens_balance = await this.chain_helper.readBalance(address, environment.config.original_package_id + '::kyovy::KYOVY');
      this.tokens_balance.set(tokens_balance);
    }
  }

  /**
   * Connect to wallet
   * 
   * @param id
   */
  private async connectToWallet(id: string): Promise<ConnectModel> {
    let response = new ConnectModel();

    try {
      let wallet = this.findWallet(id);
      if (wallet) {
        const connect_feature = wallet.features['standard:connect'] as StandardConnectFeature['standard:connect'];
        let connection = await connect_feature.connect();
        if (connection.accounts.length > 0) {
          let accounts = connection.accounts.map(x => x.address);
          response.pass(accounts);
        } else {
          response.fail(id + ' is not configured with accounts');
        }
      } else {
        response.fail(id + ' is not installed');
      }
    } catch (error) {
      response.fail('Could not connect to ' + id);
    }

    return response;
  }

  /**
   * Disconnect from wallet
   * 
   * @param id
   */
  private async disconnectFromWallet(id: string) {
    try {
      let wallet = this.findWallet(id);
      if (wallet) {
        const disconnect_feature = wallet.features['standard:disconnect'] as StandardDisconnectFeature['standard:disconnect'];
        await disconnect_feature.disconnect();
      }
    } catch (error) {
      console.error('Could not disconnect from ' + id);
    }
  }

  /**
   * Execute transaction
   * 
   * @param id
   * @param sender
   * @param transaction
   */
  private async executeTransaction(id: string, sender: string, transaction: Transaction): Promise<ExecuteModel> {
    let response = new ExecuteModel();

    try {
      let wallet = this.findWallet(id);
      if (wallet) {
        const connect_feature = wallet.features['standard:connect'] as StandardConnectFeature['standard:connect'];
        let connection = await connect_feature.connect();
        if (connection.accounts.length > 0) {
          let account = connection.accounts.find(x => x.address == sender);
          if (account) {
            let client = new SuiClient({ url: this.findRpcUrl() });

            transaction.setSender(sender);
            await transaction.build({ client: client });

            const sign_feature = wallet.features['sui:signTransaction'] as SuiSignTransactionFeature['sui:signTransaction'];
            let signed = await sign_feature.signTransaction({
              account: account,
              transaction: transaction,
              chain: `sui:${environment.configuration}`,
            });

            let options = { showObjectChanges: true, showEvents: true };
            let result = await client.executeTransactionBlock({
              transactionBlock: signed.bytes,
              signature: signed.signature,
              options: options
            });

            if (result.rawEffects) {
              let effects = Uint8Array.from(result.rawEffects);
              const report_feature = wallet.features['sui:reportTransactionEffects'] as SuiReportTransactionEffectsFeature['sui:reportTransactionEffects'];
              report_feature.reportTransactionEffects({
                effects: toBase64(effects),
                account: account,
                chain: `sui:${environment.configuration}`,
              });
            }

            await client.waitForTransaction({ digest: result.digest });

            let digest = result.digest;
            let events = result.events ? result.events : [];

            response.pass(digest, events);
          } else {
            response.fail(id + ' is not configured with the selected account');
          }
        } else {
          response.fail(id + ' is not configured with accounts');
        }
      } else {
        response.fail(id + ' is not installed');
      }
    } catch (error) {
      response.fail('Could not send transaction using ' + id);
    }

    return response;
  }

  /**
   * Find jsonrpc url
   */
  private findRpcUrl() {
    let url = environment.jsonrpc.default;

    let provider = this.setting_helper.jsonrpc();
    switch (provider) {
      case 'foundation':
      case 'suiscan':
      case 'publicnode':
      case 'blastapi':
        url = environment.jsonrpc[provider];
        break;
    }

    return url;
  }

  /**
   * Find wallet
   * 
   * @param id
   */
  private findWallet(id: string) {
    let wallets = getWallets().get();
    let wallet = wallets.find(x => x.name == id && x.features['standard:connect'] && x.features['sui:signTransaction']);
    return wallet;
  }
}
